import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiPlay, HiSparkles, HiArrowLeft } from 'react-icons/hi2'
import videoApi from '../services/videoApi'
import VideoCard from '../components/tutorials/VideoCard'
import VideoModal from '../components/tutorials/VideoModal'
import CategoryFilter from '../components/tutorials/CategoryFilter'
import SearchBar from '../components/tutorials/SearchBar'
import DevelopedBy from '../components/common/DevelopedBy'

/**
 * TutorialsPage Component
 * Main page for browsing and watching tutorial videos
 */
const TutorialsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [videos, setVideos] = useState([])
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedDifficulty, setSelectedDifficulty] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Fetch categories on mount
   */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await videoApi.getCategories()
        // Handle custom API response format
        const categoriesData = response.data?.data || response.data || []
        setCategories(Array.isArray(categoriesData) ? categoriesData : [])
      } catch (err) {
        console.error('Error fetching categories:', err)
        setCategories([])
      }
    }
    fetchCategories()
  }, [])

  /**
   * Fetch videos based on filters
   */
  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true)
      setError(null)
      try {
        const params = {}
        if (selectedCategory) params.category = selectedCategory
        if (selectedDifficulty) params.difficulty = selectedDifficulty
        if (searchQuery) params.search = searchQuery

        const response = await videoApi.getVideos(params)
        // Handle custom API response format
        const videosData = response.data?.data || response.data || []
        setVideos(Array.isArray(videosData) ? videosData : [])
      } catch (err) {
        console.error('Error fetching videos:', err)
        setError(t('tutorials.loadFailed'))
        setVideos([])
      } finally {
        setLoading(false)
      }
    }

    // Debounce search query
    const timeoutId = setTimeout(fetchVideos, searchQuery ? 500 : 0)
    return () => clearTimeout(timeoutId)
  }, [selectedCategory, selectedDifficulty, searchQuery])

  /**
   * Handle video card click
   */
  const handleVideoClick = (video) => {
    setSelectedVideo(video)
  }

  /**
   * Close video modal
   */
  const handleCloseModal = () => {
    setSelectedVideo(null)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="fixed top-6 start-6 z-50">
        <button
          onClick={() => navigate('/landing')}
          className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-[#29AECB] text-gray-700 hover:text-white rounded-lg shadow-lg transition-all duration-300 font-semibold group"
        >
          <HiArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300 rtl:rotate-180 rtl:group-hover:translate-x-1" />
          <span>{t('tutorials.backToHome')}</span>
        </button>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#29AECB] via-[#01DCBA] to-[#748f89] text-white py-12 md:py-16 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <HiPlay className="w-10 h-10 md:w-12 md:h-12" />
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">
                {t('tutorials.title')}
              </h1>
            </div>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              {t('tutorials.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="mb-8">
          <SearchBar value={searchQuery} onChange={setSearchQuery} />
        </div>

        {/* Content Grid */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - Categories & Filters */}
          <aside className="lg:w-72">
            <CategoryFilter
              categories={categories}
              selected={selectedCategory}
              onSelect={setSelectedCategory}
              selectedDifficulty={selectedDifficulty}
              onDifficultyChange={setSelectedDifficulty}
            />
          </aside>

          {/* Videos Grid */}
          <main className="flex-1">
            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#29AECB]"></div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-danger-soft border border-danger/30 rounded-xl p-6 text-center">
                <p className="text-danger">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  {t('tutorials.retry')}
                </button>
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && videos.length === 0 && (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <HiSparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('tutorials.noVideos')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery
                    ? t('tutorials.noVideosSearch', { query: searchQuery })
                    : t('tutorials.noVideosHint')}
                </p>
                {(searchQuery || selectedCategory || selectedDifficulty) && (
                  <button
                    onClick={() => {
                      setSearchQuery('')
                      setSelectedCategory(null)
                      setSelectedDifficulty(null)
                    }}
                    className="px-6 py-2 bg-[#29AECB] hover:bg-[#01DCBA] text-white rounded-lg transition-colors"
                  >
                    {t('tutorials.clearFilters')}
                  </button>
                )}
              </div>
            )}

            {/* Videos Grid */}
            {!loading && !error && videos.length > 0 && (
              <>
                <div className="mb-4 text-sm text-gray-600">
                  {t('tutorials.foundCount', { count: videos.length })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {videos.map((video) => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      onClick={() => handleVideoClick(video)}
                    />
                  ))}
                </div>
              </>
            )}
          </main>
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <VideoModal video={selectedVideo} onClose={handleCloseModal} />
      )}

      {/* Footer — developer credit (shared component, brand-kit compliant) */}
      <footer className="bg-gray-900 text-white py-6 px-4 mt-12">
        <div className="max-w-7xl mx-auto flex justify-center text-sm">
          <DevelopedBy tone="dark" />
        </div>
      </footer>
    </div>
  )
}

export default TutorialsPage
